import React, { useEffect, useRef, useState } from "react";
import Accordion from "../../common/accordion";
import IPRTable from "./inflow-calc-table";
import { Info } from "../../../assets/icons";
import useBasicCalculationsFacade from "../../../facade/calculation/calculation-output/basic-calculation.facade";
import { useAppSelector } from "../../../store/hooks/StoreHooks";
import DepletionCurveGraph from "./charts/depletion-curve";
import {
    DepletionCurveGraphData,
    IPRGraphData,
} from "../../../store/model/calculations/calculation";
import IPRCurveGraph from "./charts/wellInfo-curve";
import SwitchButton from "../../common/switch-button";
import html2canvas from "html2canvas";
import useCollapseFacade from "../../../facade/collapse.Facade";
import { LABELS } from "../../../constants/labels/basicCals.labels";
import TextShortInput from "../../common/TextShortInput";

const BasicCalculations: React.FC = () => {
    const {
        depletionCurve,
        wellInflowData,
        basicTDHCalculation,
        IPRSelection,
        iprCurve,
        depletionPercentage,
        depletionPressure,
        futureProductionRate,
        showIPRPlotInMainReport,
        showDepletionPlotInMainReport,
    } = useAppSelector((state) => state.basicCalculations);
    const [shutinPressure, setShutinPressure] = useState<number | string>(depletionPressure ?? "");
    const [ProductionRate, setProductionRate] = useState<number | string>(futureProductionRate);
    const [desiredPIP, setDesiredPIP] = useState<number | string>(basicTDHCalculation?.desrdPIP ?? "");

    const {
        updateBasicDesrdPIP,
        updateBasicIPRDepletionPressure,
        updateBasicIPRFutureProdRate,
        updateShowIPRPlotInMainReport,
        updateShowDepletionPlotInMainReport
    } = useBasicCalculationsFacade();
    const iprChartRef = useRef<HTMLDivElement | null>(null);
    const depletionChartRef = useRef<HTMLDivElement | null>(null);

    const defaultIPRData: IPRGraphData = {
        wiggins: [],
        vogel: [],
        constantPi: [],
        compositePi: [],
        x_Plot_MaxValue: 0,
        y_PlotMaxValue: 0,
        x_Plot_Interval: 0,
        y_Plot_Interval: 0,
    };

    const defaultData: DepletionCurveGraphData = {
        wiggins: [],
        depletion10Percent: [],
        depletion20Percent: [],
        depletion30Percent: [],
        depletion40Percent: [],
        depletion50Percent: [],
        depletion60Percent: [],
        depletion70Percent: [],
        generalDepletion: [],
        x_Plot_MaxValue: 0,
        y_Plot_MaxValue: 0,
        x_Plot_Interval: 0,
        y_Plot_Interval: 0,
    };

    const handleWellChartDownload = () => {
        if (iprChartRef.current) {
            html2canvas(iprChartRef.current, {
                backgroundColor: "#253040",
            }).then((canvas) => {
                const imgUrl = canvas.toDataURL("image/png");

                const link = document.createElement("a");
                link.href = imgUrl;
                link.download = "IPR-Plots.png";
                link.click();
            });
        }
    };

    useEffect(() => {
        if (depletionPressure) {
            setShutinPressure(depletionPressure);
        }
    }, [depletionPressure]);

    const handleDepletionChartDownload = () => {
        if (depletionChartRef.current) {
            html2canvas(depletionChartRef.current, {
                backgroundColor: "#384252",

            }).then((canvas) => {
                const imgUrl = canvas.toDataURL("image/png");

                const link = document.createElement("a");
                link.href = imgUrl;
                link.download = "Depletion-Plot.png";
                link.click();
            });
        }
    };

    const handleToggleButtonWell = (checked: boolean) => {
        updateShowIPRPlotInMainReport(checked);
    };

    const handleToggleButtonDepletion = (checked: boolean) => {
        updateShowDepletionPlotInMainReport(checked);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const regex = /^[0-9]+(\.[0-9]*)?$/;

        const isValid = regex.test(value);
        if (isValid || value === "") {
            if (name === "shutinPressure") {
                setShutinPressure(value);
            } else if (name === "productionRate") {
                setProductionRate(value);
            } else if (name === "desiredPIP") {
                setDesiredPIP(value);
            }
        }
    };

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        if (name === "shutinPressure") {
            updateBasicIPRDepletionPressure(Number(shutinPressure));
        } else if (name === "productionRate") {
            updateBasicIPRFutureProdRate(Number(ProductionRate));
        } else if (name === "desiredPIP") {
            updateBasicDesrdPIP(Number(desiredPIP));
        }
        return;
    };

    const {
        wellInflowCalculationsCollapse,
        wellIprCalculationsCollapse,
        tdhCalculationsCollapse,
    } = useAppSelector((state) => state?.collapse);

    const {
        updateWellInflowCalculationsCollapse,
        updateWellIprCalculationsCollapse,
        updateTdhCalculationsCollapse,
    } = useCollapseFacade();

    const handleCollapseChange = (key: boolean, name: string) => {
        if (name === "wellInflow") updateWellInflowCalculationsCollapse(key);
        else if (name === "wellIpr") updateWellIprCalculationsCollapse(key);
        else if (name === "tdh") updateTdhCalculationsCollapse(key);
    };

    return (
        <>
            <Accordion
                title={LABELS?.wellInflowCals}
                tooltipText={LABELS?.tooltipText}
                link={'/help?tab=Basic-Calculations#WIF'}
                isOpen={wellInflowCalculationsCollapse}
                onToggle={() =>
                    handleCollapseChange(!wellInflowCalculationsCollapse, "wellInflow")
                }
                content={
                    <div className="p-[16px]">
                        <IPRTable
                            wellInflowData={wellInflowData || []}
                            iprSelected={IPRSelection ?? "Wiggins"}
                        />
                    </div>
                }
            />
            <div className="mb-[16px]" />
            <Accordion
                title={LABELS?.wellIPRCals}
                isOpen={wellIprCalculationsCollapse}
                link={'/help?tab=Basic-Calculations#IPR'}
                tooltipText={LABELS?.tooltipText}
                onToggle={() =>
                    handleCollapseChange(!wellIprCalculationsCollapse, "wellIpr")
                }
                content={
                    <div className="p-[16px]">
                        <div className="p-[16px]">
                            <div className="flex justify-between">
                                <div>
                                    <label className="text-[18px] font-semibold text-neutral-25">
                                        {LABELS?.wellInflowPerformance}
                                    </label>
                                </div>
                                <div className="flex">
                                    <SwitchButton
                                        checked={showIPRPlotInMainReport}
                                        onChange={handleToggleButtonWell}
                                        label={LABELS?.includeInMainReport}
                                    />
                                    <button
                                        onClick={handleWellChartDownload}
                                        className="ml-[16px] border-[1px] border-solid border-[#4A5463] py-[8px] px-[14px] inline-flex justify-center items-center gap-[8px] rounded-[8px] bg-transparent text-neutral-25"
                                    >
                                        {LABELS?.exportIPRPlot}
                                    </button>
                                </div>
                            </div>
                            <div className="mb-[15px] pt-[30px]" ref={iprChartRef}>
                                <IPRCurveGraph data={iprCurve ? iprCurve : defaultIPRData} />
                            </div>
                            {IPRSelection !== "ConstantPI" && IPRSelection !== "Composite" && (<div className="flex justify-between items-center mb-[32px]">
                                <div>
                                    <div className="flex items-center mb-[16px]">
                                        <label className="text-neutral-25 font-medium mr-2">
                                            {LABELS?.futureShutInPressure}
                                        </label>
                                        <div className="flex items-center">
                                            <TextShortInput
                                                type="text"
                                                value={shutinPressure || ""}
                                                onChange={handleOnChange}
                                                onBlur={(e: any) => handleOnBlur(e)}
                                                name={"shutinPressure"}
                                                className="w-[80px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500"
                                            />
                                            <span className="ml-2 text-neutral-25">{LABELS?.psiUnit}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <label className="text-neutral-25 font-medium mr-2">
                                            {LABELS?.futureProdRate}
                                        </label>
                                        <div className="flex items-center">
                                            <TextShortInput
                                                type="text"
                                                value={ProductionRate}
                                                onChange={handleOnChange}
                                                name={"productionRate"}
                                                className="w-[80px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500"
                                            />
                                            <span className="ml-2 text-neutral-25">{LABELS?.bpdUnit}</span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-neutral-25 font-medium">
                                        {LABELS?.depletionPercentage} <span className="font-bold">{depletionPercentage ?? 0}</span>
                                    </p>
                                </div>
                            </div>)}
                            <div className="flex justify-between">
                                <div>
                                    <label className="text-[18px] font-semibold text-neutral-25">
                                        {LABELS?.depletionCUrve}
                                    </label>
                                </div>
                                <div className="flex">
                                    <SwitchButton
                                        checked={showDepletionPlotInMainReport}
                                        onChange={handleToggleButtonDepletion}
                                        label={LABELS?.includeInMainReport}
                                    />
                                    <button
                                        onClick={handleDepletionChartDownload}
                                        className="ml-[16px] border-[1px] border-solid border-[#4A5463] py-[8px] px-[14px] inline-flex justify-center items-center gap-[8px] rounded-[8px] bg-transparent text-neutral-25"
                                    >
                                        {LABELS?.exportDepletionPlot}
                                    </button>
                                </div>
                            </div>
                            <div className="mb-[15px] pt-[30px]" ref={depletionChartRef}>
                                <DepletionCurveGraph data={depletionCurve ?? defaultData} />
                            </div>
                        </div>
                    </div>
                }
            />
            <div className="mb-[16px]" />
            <Accordion
                title={LABELS?.tdhCals}
                tooltipText={LABELS?.tooltipText}
                link={'/help?tab=Basic-Calculations#TDH'}
                isOpen={tdhCalculationsCollapse}
                onToggle={() =>
                    handleCollapseChange(!tdhCalculationsCollapse, "tdh")
                }
                content={
                    <div className="flex flex-col gap-[16px] p-4">
                        <div className="flex justify-center items-start gap-4 w-full">
                            <div className="flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2">
                                {[
                                    {
                                        label: `${LABELS?.tubingHead}`,
                                        value: basicTDHCalculation?.tubingHead ?? "--",
                                    },
                                    {
                                        label: `${LABELS?.frictionLoss}`,
                                        value: basicTDHCalculation?.frictionLoss ?? "--",
                                    },
                                    {
                                        label: `${LABELS?.headAtPumpIntake}`,
                                        value: basicTDHCalculation?.pip ?? "--",
                                    },
                                    {
                                        label: `${LABELS?.mainPumpNVL}`,
                                        value: basicTDHCalculation?.productionLevel ?? "--",
                                    },
                                ].map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400"
                                    >
                                        <label className="text-neutral-25">{item.label}</label>
                                        <label className="text-neutral-25">{item.value} ft</label>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center py-[9px] px-[24px] w-full">
                                    <label className="text-neutral-25">Desired PIP:</label>
                                    <div className="flex items-center">
                                        <TextShortInput
                                            type="text"
                                            value={desiredPIP}
                                            onChange={handleOnChange}
                                            name={"desiredPIP"}
                                            className="w-[80px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500"
                                            onBlur={(e: any) => handleOnBlur(e)}
                                        />
                                        <span className="ml-2 text-neutral-25">{LABELS?.psiUnit}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2">
                                {[
                                    {
                                        label: `${LABELS?.requiredMainPumpTDH}`,
                                        value: basicTDHCalculation?.tdh ?? "--",
                                    },
                                    {
                                        label: `${LABELS?.requiredSystemTDH}`,
                                        value: basicTDHCalculation?.systemTDH ?? "--",
                                    },
                                    {
                                        label: `${LABELS?.mainPumpIntakePressure}`,
                                        value: basicTDHCalculation?.pipNew ?? "--",
                                    },
                                    { label: `${LABELS?.newTDH}`, value: basicTDHCalculation?.newTDH ?? "--" },
                                ].map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400 last:border-b-0"
                                    >
                                        <label className="text-neutral-25">{item.label}</label>
                                        <label className="text-neutral-25">
                                            {item.value} {index === 2 ? "psi" : "ft"}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-start items-center p-[16px] gap-[12px] rounded-[8px] border-[1px] bg-primary-600 border-darkcolor-400">
                            <Info className="mr-2" />
                            <p className="text-[14px] font-bold leading-[20px] text-neutral-25">
                                {LABELS?.note}
                            </p>
                        </div>
                    </div>
                }
            />
        </>
    );
};

export default BasicCalculations;
